
import { MoreHorizontal, Pencil, Trash } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ComponentType } from "@/utils/types/componentTypes";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2 } from "lucide-react";

interface ComponentTypesTableProps {
  componentTypes: ComponentType[];
  loading: boolean;
  onSort: (column: string) => void;
  sortColumn: string;
  sortDirection: "asc" | "desc";
  onEdit: (componentType: ComponentType) => void;
  onDelete: (componentType: ComponentType) => void;
  onAdd?: () => void;
}

export function ComponentTypesTable({
  componentTypes,
  loading,
  onSort,
  sortColumn,
  sortDirection,
  onEdit,
  onDelete,
}: ComponentTypesTableProps) {
  const handleSort = (column: string) => {
    onSort(column);
  };

  const getIconUrl = (iconName: string | null) => {
    if (!iconName) return null;
    
    try {
      // iconName should contain the full path like "icons/lucide-icon" or "tech-icons/adobe.svg"
      return `/assets/${iconName}`;
    } catch (error) {
      console.error(`Erro ao gerar URL para o ícone ${iconName}:`, error);
      return null;
    }
  };

  if (loading) {
    return <TableLoadingState />;
  }

  return (
    <div className="bg-card rounded-lg overflow-hidden border border-border/40">
      <Table>
        <TableHeader>
          <TableRow className="border-b border-border">
            <TableHead 
              className="font-semibold text-muted-foreground cursor-pointer hover:text-foreground"
              onClick={() => handleSort("group_name")}
            >
              Categoria
            </TableHead>
            <TableHead 
              className="font-semibold text-muted-foreground cursor-pointer hover:text-foreground" 
              onClick={() => handleSort("name")}
            >
              Nome
            </TableHead>
            <TableHead className="font-semibold text-muted-foreground">Ícone</TableHead>
            <TableHead className="font-semibold text-muted-foreground text-right">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {componentTypes.length === 0 ? (
            <TableRow>
              <TableCell colSpan={4} className="text-center py-12">
                <p className="text-sm text-muted-foreground">Nenhum tipo de componente encontrado.</p>
              </TableCell>
            </TableRow>
          ) : (
            componentTypes.map((componentType) => {
              const iconUrl = componentType.icon ? getIconUrl(componentType.icon) : null;
              
              return (
                <TableRow key={componentType.id} className="hover:bg-muted/20">
                  <TableCell className="text-muted-foreground">{componentType.group_name || '—'}</TableCell>
                  <TableCell className="font-medium">{componentType.name}</TableCell>
                  <TableCell className="py-4">
                    {componentType.icon ? (
                      <img 
                        src={iconUrl || ''} 
                        alt={componentType.name} 
                        className="h-8 w-8 object-contain"
                        onError={(e) => {
                          console.error(`Falha ao carregar ícone na tabela: ${componentType.icon}, URL: ${iconUrl}`);
                          (e.target as HTMLImageElement).src = '/placeholder.svg';
                        }}
                      />
                    ) : (
                      <div className="h-8 w-8 flex items-center justify-center text-muted-foreground">—</div>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem 
                          onClick={() => onEdit(componentType)}
                          className="cursor-pointer"
                        >
                          <Pencil className="mr-2 h-4 w-4" />
                          Editar
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          className="cursor-pointer text-destructive"
                          onClick={() => onDelete(componentType)}
                        >
                          <Trash className="mr-2 h-4 w-4" />
                          Excluir
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </div>
  );
}

function TableLoadingState() {
  return (
    <div className="bg-card rounded-lg overflow-hidden border border-border/40">
      <Table>
        <TableHeader>
          <TableRow className="border-b border-border">
            <TableHead className="font-semibold text-muted-foreground">Categoria</TableHead>
            <TableHead className="font-semibold text-muted-foreground">Nome</TableHead>
            <TableHead className="font-semibold text-muted-foreground">Ícone</TableHead>
            <TableHead className="font-semibold text-muted-foreground text-right">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          <TableRow>
            <TableCell colSpan={4} className="text-center py-12">
              <div className="flex justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
              <p className="mt-2 text-sm text-muted-foreground">Carregando tipos de componentes...</p>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </div>
  );
}
